import { Link } from "react-router-dom";
import { Phone, MapPin, ChevronRight, Shovel, Home, Mountain, Truck } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOMeta from "@/components/SEOMeta";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";
import FAQ from "@/components/FAQ";

const faqData = [
  {
    question: "What's included in site excavation?",
    answer: "Our excavation services include site clearing, topsoil removal and stockpiling, grading to proper elevations, trenching for utilities, backfilling, and final grading. We handle all earthwork needs for your project."
  },
  {
    question: "How long does excavation take?",
    answer: "A typical residential foundation excavation takes 1-3 days depending on size and soil conditions. Larger commercial projects may take 1-2 weeks. We provide accurate timelines after site assessment."
  },
  {
    question: "Do you handle permits and locates?",
    answer: "Yes, we coordinate all necessary excavation permits and utility locates. Ontario One Call locates are completed before any digging to ensure safe work around underground utilities."
  },
  {
    question: "What happens to the excavated soil?",
    answer: "We stockpile topsoil for reuse in landscaping, use suitable fill for backfilling, and remove excess material. We can also coordinate bringing in engineered fill if needed for proper compaction."
  }
];

const excavationServices = [
  {
    title: "Foundation Excavation",
    description: "Precise excavation for residential and commercial building foundations with proper depth and slope.",
    icon: Home,
    features: ["Basement excavation", "Grade verification", "Foundation prep", "Waterproofing access"]
  },
  {
    title: "Site Grading & Leveling",
    description: "Land grading for proper drainage, lot leveling, and site preparation for construction.",
    icon: Mountain,
    features: ["Rough grading", "Fine grading", "Drainage slopes", "Lot preparation"]
  },
  {
    title: "Trenching & Utilities",
    description: "Utility trenching for water, sewer, electrical, and gas line installations.",
    icon: Shovel,
    features: ["Water lines", "Sewer connections", "Electrical conduit", "Gas lines"]
  },
  {
    title: "Site Clearing",
    description: "Land clearing, tree removal, and debris hauling for new construction and development.",
    icon: Truck,
    features: ["Tree removal", "Brush clearing", "Stump grinding", "Debris hauling"]
  }
];

const serviceAreas = [
  { name: "Niagara Region", link: "/locations" },
  { name: "Hamilton", link: "/locations/hamilton" },
  { name: "Toronto & GTA", link: "/locations/toronto" },
  { name: "London", link: "/locations/london" }
];

export default function ExcavationServices() {
  return (
    <>
      <SEOMeta
        title="Excavation & Site Work Contractor | Niagara • Hamilton • GTA"
        description="Site clearing and earthmoving for residential and commercial projects in Niagara, Hamilton & the GTA. Grading, trenching and lot prep."
        keywords="excavation contractor Niagara, Hamilton excavation services, GTA site work, grading, trenching, lot clearing, backfilling"
        canonicalUrl="https://blueknightcontracting.ca/services/excavation"
      />
      <LocalBusinessSchema
        locationName="Excavation Services"
        addressLocality="Niagara Falls"
        addressRegion="ON"
        description="Professional excavation and site work services"
        services={["Foundation Excavation", "Site Grading", "Trenching", "Land Clearing", "Earthmoving"]}
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        {/* Hero Section */}
        <section className="relative py-20 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Excavation & Site Work
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Professional earthmoving services across Niagara, Hamilton & the GTA
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button asChild size="lg" className="gap-2">
                <Link to="/contact">
                  Get Free Quote <ChevronRight className="h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="gap-2">
                <a href="tel:+12899370797">
                  <Phone className="h-5 w-5" /> (289) 937-0797
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Excavation Services</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                From foundation digging to final grading, we provide complete site work for any project
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {excavationServices.map((service, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <ChevronRight className="h-4 w-4 text-primary" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Why Choose Blue Knight?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle>Modern Equipment</CardTitle>
                  <CardDescription>
                    Well-maintained excavators, loaders, and grading equipment for efficient, precise work
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Experienced Operators</CardTitle>
                  <CardDescription>
                    Skilled operators with years of experience in residential and commercial excavation
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Safety First</CardTitle>
                  <CardDescription>
                    Full utility locates, proper shoring, and strict safety protocols on every project
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Service Areas</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {serviceAreas.map((area, index) => (
                <Link key={index} to={area.link}>
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardHeader>
                      <MapPin className="h-8 w-8 text-primary mb-2" />
                      <CardTitle className="text-lg">{area.name}</CardTitle>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <FAQ faqs={faqData} />
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Card className="bg-gradient-to-br from-primary/10 to-accent/10">
              <CardHeader>
                <CardTitle className="text-3xl mb-4">Ready to Break Ground?</CardTitle>
                <CardDescription className="text-lg mb-6">
                  Contact us for a site visit and detailed excavation quote
                </CardDescription>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg">
                    <Link to="/contact">Request Quote</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <a href="tel:+12899370797">(289) 937-0797</a>
                  </Button>
                </div>
              </CardHeader>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
